import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardImg,
  Badge,
  Spinner,
  Input,
  Alert,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CampaignService from "../../../services/OrderingServices/CampaignService";
import AdsService from "../../../services/OrderingServices/AdsService";
import CampaignAssignService from "../../../services/OrderingServices/CampaignAssignService";
import Select from "react-select";
import { useLocation } from "react-router-dom";

// ----- Reusable Hook for Drag-and-Drop Reorder -----
const useReorderList = (initialList = []) => {
  const [items, setItems] = useState(initialList);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (index, e) => {
    setDraggedIndex(index);
    if (e.target) e.target.style.opacity = 0.5;
  };

  const handleDragEnd = (e) => {
    if (e.target) e.target.style.opacity = 1;
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (index, e) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (dropIndex, e) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(dropIndex, 0, draggedItem);

    setItems(newItems);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return {
    items,
    setItems,
    draggedIndex,
    dragOverIndex,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
  };
};

// ----- Main Component -----
const AssignMedia = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [ads, setAds] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [adsSearch, setAdsSearch] = useState("");
  const [showReorderMode, setShowReorderMode] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const location = useLocation();

  const CDN_BASE_URL = window.APP_CONFIG?.api3?.baseapi || "";

  // Reorder hook
  const {
    items: selectedAds,
    setItems: setSelectedAds,
    draggedIndex,
    dragOverIndex,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
  } = useReorderList([]);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const [campaignRes, adsRes] = await Promise.all([
        CampaignService.campaignsList({ pageSize: 100 }),
        AdsService.adsList({ pageSize: 100 }),
      ]);

      const campaignList = campaignRes?.data || [];
      setCampaigns(campaignList);
      setAds(adsRes?.data || []);

      if (location.state?.campaignId) {
        const foundCampaign = campaignList.find(
          (c) => c.id === location.state.campaignId,
        );

        if (foundCampaign) {
          handleCampaignSelect({
            value: foundCampaign.id,
            label: foundCampaign.name,
          });
        }
      }
    } catch (error) {
      toast.error("Failed to load data");
      console.error(error);
    }
  }

  // Fetch campaign details and pre-select its ads
  const handleCampaignSelect = async (option) => {
    try {
      setSelectedCampaign(option ? option.value : null);

      // If a campaign is selected, fetch its details to get assigned ads
      if (option && option.value) {
        const campaignDetails = await CampaignService.getCampaignById(
          option.value,
        );

        // If campaign has ads, find them in the full ads list and pre-select them
        if (campaignDetails.ads && campaignDetails.ads.length > 0) {
          const campaignAdIds = campaignDetails.ads.map((ad) => ad.id);
          const adsToSelect = ads.filter((ad) => campaignAdIds.includes(ad.id));
          setSelectedAds(adsToSelect);
        } else {
          setSelectedAds([]);
        }
      } else {
        setSelectedAds([]);
      }
    } catch (error) {
      toast.error("Failed to fetch campaign details");
      console.error(error);
      setSelectedCampaign(null);
      setSelectedAds([]);
    }
  };

  const campaignOptions = campaigns.map((campaign) => ({
    value: campaign.id,
    label: campaign.description
      ? `${campaign.name} - ${campaign.description}`
      : campaign.name,
  }));

  const getMediaUrl = (ad) =>
    ad.mediaType === "Video" && ad.thumbnailFile
      ? `${CDN_BASE_URL}${ad.thumbnailFile}`
      : `${CDN_BASE_URL}${ad.mediaPath}`;

  const toggleAdSelection = (ad) => {
    const index = selectedAds.findIndex((a) => a.id === ad.id);
    if (index > -1) {
      setSelectedAds(selectedAds.filter((a) => a.id !== ad.id));
    } else {
      setSelectedAds([...selectedAds, ad]);
    }
  };

  const getAdPosition = (adId) => {
    const index = selectedAds.findIndex((a) => a.id === adId);
    return index > -1 ? index + 1 : null;
  };

  const handleAssign = async () => {
    if (!selectedCampaign || selectedAds.length === 0) {
      toast.error("Please select a campaign and at least one ad");
      return;
    }
    try {
      setAssigning(true);
      await CampaignAssignService.assignAdsToCampaigns({
        campaignIds: [selectedCampaign],
        adIds: selectedAds.map((ad) => ad.id),
      });
      toast.success(
        `Successfully assigned ${selectedAds.length} media to campaign!`,
      );
      setSelectedCampaign(null);
      setSelectedAds([]);
      setShowReorderMode(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to assign media");
      console.error(error);
    } finally {
      setAssigning(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedAds([]);
    setShowReorderMode(false);
  };

  const filteredAds = ads.filter((a) =>
    a.title?.toLowerCase().includes(adsSearch.toLowerCase()),
  );

  const displayAds = showReorderMode ? selectedAds : filteredAds;

  if (!ads) {
    return (
      <div className="page-content">
        <Container fluid className="text-center py-5">
          <Triangle color="#2c3e50" height={80} width={80} />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Assign Media to Campaign"
          pageTitle="Ads Management"
        />

        {/* Campaign Selection */}
        <Row className="">
          <Col lg="12">
            <Card className="border">
              <CardBody className="p-4">
                <h6 className="fw-bold mb-3">Select Campaign</h6>
                <Row className="align-items-end justify-content-between">
                  <Col md="3">
                    <Select
                      options={campaignOptions}
                      value={campaignOptions.find(
                        (option) => option.value === selectedCampaign,
                      )}
                      onChange={handleCampaignSelect}
                      placeholder="Choose a campaign..."
                      isClearable
                    />
                  </Col>
                  <Col md="4" className="text-end">
                    {selectedCampaign && (
                      <Badge color="success" pill className="px-3 py-2">
                        <i className="ri-checkbox-circle-fill me-1" />
                        Campaign Selected
                      </Badge>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Media Selection Controls */}
        {selectedCampaign && (
          <Row className="mb-4">
            <Col lg="12">
              <Alert
                color="secondary"
                className="d-flex align-items-center mb-0"
              >
                <i className="ri-information-line fs-4 me-2" />
                <div className="flex-grow-1">
                  <strong>{selectedAds.length} media selected</strong>
                  {selectedAds.length > 0 && (
                    <span className="text-muted ms-2">
                      • Click cards to select/deselect • Drag to reorder in
                      reorder mode
                    </span>
                  )}
                </div>
                <div className="d-flex gap-2">
                  {selectedAds.length > 0 && (
                    <>
                      <Button
                        color="primary"
                        outline
                        size="sm"
                        onClick={() => setShowReorderMode(!showReorderMode)}
                      >
                        <i
                          className={`ri-${
                            showReorderMode ? "grid" : "draggable"
                          }-line me-1`}
                        />
                        {showReorderMode ? "Show All" : "Reorder"}
                      </Button>
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        onClick={handleClearSelection}
                      >
                        <i className="ri-close-circle-line me-1" />
                        Clear
                      </Button>
                    </>
                  )}
                  <Button
                    color="dark"
                    size="sm"
                    onClick={handleAssign}
                    disabled={assigning || selectedAds.length === 0}
                  >
                    {assigning ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Assigning...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line me-1" />
                        Assign ({selectedAds.length})
                      </>
                    )}
                  </Button>
                </div>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Search */}
        {selectedCampaign && !showReorderMode && (
          <Row className="mb-4">
            <Col lg="12">
              <div className="position-relative">
                <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Input
                  type="text"
                  placeholder="Search media by title..."
                  value={adsSearch}
                  onChange={(e) => setAdsSearch(e.target.value)}
                  className="ps-5"
                />
              </div>
            </Col>
          </Row>
        )}

        {/* Media Grid */}
        {selectedCampaign && (
          <Row className="g-3">
            {displayAds.length === 0 ? (
              <Col lg="12">
                <Card className="border">
                  <CardBody className="text-center py-5">
                    <i className="ri-inbox-line display-1 text-muted" />
                    <h5 className="mt-3 text-muted">
                      {showReorderMode ? "No media selected" : "No media found"}
                    </h5>
                    <p className="text-muted small">
                      {showReorderMode
                        ? "Select media to arrange their order"
                        : "Try adjusting your search or add new media"}
                    </p>
                  </CardBody>
                </Card>
              </Col>
            ) : (
              displayAds.map((ad, index) => {
                const position = getAdPosition(ad.id);
                const isSelected = position !== null;
                const isDragging = draggedIndex === index;
                const isDragOver = dragOverIndex === index;

                return (
                  <Col
                    key={ad.id}
                    xs="12"
                    sm="6"
                    md="4"
                    lg="3"
                    xl="2"
                    className="d-flex"
                  >
                    <Card
                      className={`w-100 border ${
                        isSelected ? "border-primary shadow-sm" : ""
                      } ${isDragOver ? "border-info bg-light" : ""}`}
                      draggable={showReorderMode && isSelected}
                      onDragStart={(e) =>
                        showReorderMode &&
                        isSelected &&
                        handleDragStart(index, e)
                      }
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) =>
                        showReorderMode &&
                        isSelected &&
                        handleDragOver(index, e)
                      }
                      onDrop={(e) =>
                        showReorderMode && isSelected && handleDrop(index, e)
                      }
                      onClick={() => !showReorderMode && toggleAdSelection(ad)}
                      style={{
                        cursor: showReorderMode
                          ? isSelected
                            ? "move"
                            : "default"
                          : "pointer",
                        opacity: isDragging ? 0.5 : 1,
                      }}
                    >
                      <div className="position-relative">
                        <CardImg
                          top
                          src={getMediaUrl(ad)}
                          alt={ad.title}
                          style={{ height: "180px", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f6f8fa'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23959da5' font-family='sans-serif' font-size='14'%3ENo Image%3C/text%3E%3C/svg%3E";
                          }}
                        />

                        {showReorderMode && isSelected && (
                          <div className="position-absolute top-0 start-0 m-2">
                            <Badge color="light" className="border">
                              <i className="ri-draggable" />
                            </Badge>
                          </div>
                        )}

                        {isSelected && (
                          <div className="position-absolute top-0 end-0 m-2">
                            <Badge color="primary" pill className="px-2 py-1">
                              {position}
                            </Badge>
                          </div>
                        )}

                        <div
                          className="position-absolute start-0 m-2"
                          style={{
                            top: showReorderMode && isSelected ? "45px" : "0",
                          }}
                        >
                          <Badge color="dark" className="text-white">
                            {ad.mediaType}
                          </Badge>
                        </div>

                        {isSelected && !showReorderMode && (
                          <div className="position-absolute bottom-0 end-0 m-2">
                            <Badge color="success" pill className="px-2 py-1">
                              <i className="ri-check-line me-1" />
                              Selected
                            </Badge>
                          </div>
                        )}
                      </div>

                      <CardBody className="p-3">
                        <h6 className="mb-2 text-truncate fw-semibold">
                          {ad.title}
                        </h6>
                        {/* <div className="d-flex justify-content-between align-items-center small text-muted">
                          <span>Priority: {ad.priority}</span>
                          {ad.durationSeconds && <span>{ad.durationSeconds}s</span>}
                        </div> */}
                        {ad.advertiserName && (
                          <div className="small text-muted mt-2">
                            {ad.advertiserName}
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                );
              })
            )}
          </Row>
        )}

        {/* Empty State - No Campaign Selected */}
        {!selectedCampaign && (
          <Row>
            <Col lg="12">
              <Card className="border">
                <CardBody className="text-center py-5">
                  <i className="ri-folder-3-line display-1 text-muted" />
                  <h4 className="mt-4 fw-bold">Select a Campaign to Begin</h4>
                  <p className="text-muted">
                    Choose a campaign from the dropdown above to start assigning
                    media
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default AssignMedia;
